"use client";

import { useEffect } from "react";

// Keeps the reply being typed in this tab's session storage until the server
// has accepted it: a refused reply (someone else took the conversation, the
// confirmation was missing) comes back with its text instead of an empty
// field; Ctrl+Enter (Cmd+Enter on a Mac) sends it. Without scripts the form
// still works, without these conveniences.

export type ReplyDraftProps = { conversationId: string; fieldId: string; /** The last post stored the reply. */ sent: boolean };

function storageKey(conversationId: string): string {
  return `iron-reply-draft:${conversationId}`;
}

export function ReplyDraft({ conversationId, fieldId, sent }: ReplyDraftProps) {
  useEffect(() => {
    const field = document.getElementById(fieldId);
    if (!(field instanceof HTMLTextAreaElement)) return;
    const key = storageKey(conversationId);
    const submittedKey = `${key}:submitted`;
    try {
      if (sent) {
        const submitted = window.sessionStorage.getItem(submittedKey);
        if (submitted !== null) {
          // Consume the acknowledgement once. A later refresh or remount can
          // carry the same success notice after a new reply has been started.
          if (window.sessionStorage.getItem(key) === submitted) window.sessionStorage.removeItem(key);
          if (field.value === submitted) field.value = "";
          window.sessionStorage.removeItem(submittedKey);
        }
      }
      if (!field.value) field.value = window.sessionStorage.getItem(key) ?? "";
    } catch {
      // Storage may be unavailable (private mode); the form works without it.
    }
    const save = () => {
      try {
        if (field.value) window.sessionStorage.setItem(key, field.value);
        else window.sessionStorage.removeItem(key);
      } catch {
        // Nothing to keep then.
      }
    };
    const send = (event: KeyboardEvent) => {
      if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && !event.isComposing) {
        event.preventDefault();
        field.form?.requestSubmit();
      }
    };
    const submitted = () => {
      save();
      try {
        window.sessionStorage.setItem(submittedKey, field.value);
      } catch {
        // Submission does not depend on browser storage.
      }
    };
    const form = field.form;
    field.addEventListener("input", save);
    field.addEventListener("keydown", send);
    form?.addEventListener("submit", submitted);
    return () => {
      field.removeEventListener("input", save);
      field.removeEventListener("keydown", send);
      form?.removeEventListener("submit", submitted);
    };
  }, [conversationId, fieldId, sent]);
  return null;
}
